import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, Segments, Empty, useFlash } from '../../ui/kit.js';
import { clock } from '../../state/building.js';

import HousekeepingCart from '../../media/images/modules/housekeeping-cart.jpg';
import Lobby from '../../media/images/modules/area-lobby.jpg';
import Gym from '../../media/images/modules/area-gym.jpg';
import PoolDeck from '../../media/images/modules/area-pool-deck.jpg';
import GuestSuite from '../../media/images/modules/guest-suite.jpg';
import UnitPhoto from '../../media/images/modules/area-unit.jpg';
import Lounge from '../../media/images/modules/area-lounge.jpg';
import ElevatorLobby from '../../media/images/modules/area-elevator-lobby.jpg';

const COLUMNS = ['To clean', 'In progress', 'Inspected'];
const FILTERS = ['All', 'Common areas', 'Amenities', 'Guest suites', 'Unit turnovers'];
const ACTION = { 'To clean': 'Start cleaning', 'In progress': 'Pass inspection' };

const AREA_PHOTOS = {
    'Main lobby and mail room': Lobby,
    'Fitness center': Gym,
    'Pool deck and cabanas': PoolDeck,
    'Owners lounge and bar': Lounge,
    'Garage elevator lobbies': ElevatorLobby
};

/* Guest suites and unit turnovers share a photo per type. */
const photoOf = item => AREA_PHOTOS[item.area]
    || (item.type === 'Guest suites' ? GuestSuite : item.type === 'Unit turnovers' ? UnitPhoto : undefined);

const SEED = [
    { id: 1, area: 'Main lobby and mail room', type: 'Common areas', floor: 'Lobby', attendant: 'Marisol G.', status: 'In progress', note: 'Marble floor buff' },
    { id: 2, area: 'Fitness center', type: 'Amenities', floor: 'Floor 5', attendant: 'Yanet P.', status: 'To clean', note: 'Wipe down equipment, restock towels' },
    { id: 3, area: 'Pool deck and cabanas', type: 'Amenities', floor: 'Floor 5', attendant: 'Carlos T.', status: 'To clean', note: 'Reset loungers, clear umbrellas' },
    { id: 4, area: 'Guest suite A', type: 'Guest suites', floor: 'Floor 3', attendant: 'Marisol G.', status: 'To clean', note: 'Check-in 3:00 PM for Unit 2104' },
    { id: 5, area: 'Guest suite B', type: 'Guest suites', floor: 'Floor 3', attendant: 'Yanet P.', status: 'Inspected', note: 'Ready' },
    { id: 6, area: 'Unit 1110 turnover', type: 'Unit turnovers', floor: 'Floor 11', attendant: 'Carlos T.', status: 'In progress', note: 'Post move-out deep clean' },
    { id: 7, area: 'Unit 604 turnover', type: 'Unit turnovers', floor: 'Floor 6', attendant: 'Marisol G.', status: 'To clean', note: 'Before move-in' },
    { id: 8, area: 'Owners lounge and bar', type: 'Amenities', floor: 'Floor 38', attendant: 'Yanet P.', status: 'Inspected', note: 'Event tonight 7:00 PM' },
    { id: 9, area: 'Garage elevator lobbies', type: 'Common areas', floor: 'P1 to P3', attendant: 'Carlos T.', status: 'To clean', note: '' }
];

/* Housekeeping: common areas, amenities, guest suites and unit turnovers
   moved from the cleaning queue through inspection. */
function HousekeepingScreen() {
    const [areas, setAreas] = useState(SEED);
    const [filter, setFilter] = useState('All');
    const [flash, notify] = useFlash();

    const advance = item => {
        const next = COLUMNS[COLUMNS.indexOf(item.status) + 1];
        if (!next) return;
        setAreas(list => list.map(a => (a.id === item.id ? { ...a, status: next, at: clock() } : a)));
        notify(`${item.area} is now ${next.toLowerCase()}${next === 'Inspected' ? ' and ready for residents' : ''}.`);
    };

    const reset = item => {
        setAreas(list => list.map(a => (a.id === item.id ? { ...a, status: 'To clean', at: clock() } : a)));
        notify(`${item.area} sent back to the cleaning queue.`);
    };

    const shown = areas.filter(a => filter === 'All' || a.type === filter);
    const count = status => areas.filter(a => a.status === status).length;

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Housekeeping" title="Housekeeping board" aside={
                <Segments label="Area type" options={FILTERS} value={filter} onChange={setFilter} />
            } />

            <div className="banner">
                <img src={HousekeepingCart} alt="" />
                <p className="banner__caption">Common areas, amenities, guest suites and turnovers, cleaned and inspected.</p>
            </div>

            <Kpis items={[
                { label: 'To clean', value: count('To clean'), tone: 'warn' },
                { label: 'In progress', value: count('In progress'), tone: 'info' },
                { label: 'Inspected', value: count('Inspected'), tone: 'ok' },
                { label: 'Attendants on shift', value: new Set(areas.map(a => a.attendant)).size }
            ]} />

            {flash}

            <div className="board" style={{ '--board-cols': 3 }}>
                {COLUMNS.map(column => {
                    const cards = shown.filter(a => a.status === column);
                    return (
                        <div className="board__col" key={column}>
                            <div className="board__head">
                                <span>{column}</span>
                                <span>{cards.length}</span>
                            </div>

                            {cards.length === 0 && <Empty>Nothing here.</Empty>}

                            {cards.map((item, i) => (
                                <Panel className="board__card" key={item.id} index={i} layout>
                                    {photoOf(item) && (
                                        <img className="panel__media" src={photoOf(item)} alt={item.area} loading="lazy" />
                                    )}
                                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                        <Chip tone="muted">{item.floor}</Chip>
                                        <Chip tone="info">{item.type}</Chip>
                                    </div>
                                    <h4>{item.area}</h4>
                                    <p className="row__meta">
                                        {item.attendant}{item.note ? ` · ${item.note}` : ''}{item.at ? ` · updated ${item.at}` : ''}
                                    </p>
                                    {column === 'Inspected' ? (
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => reset(item)}>
                                            Needs another clean
                                        </button>
                                    ) : (
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => advance(item)}>
                                            {ACTION[column]}
                                        </button>
                                    )}
                                </Panel>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default HousekeepingScreen;
